import { useCallback, useEffect } from 'react'

import axios from 'axios'
import mixpanel from 'mixpanel-browser'

import {
  fetchObservationsReportDetailsChunked,
  Observation,
} from '../../../../../../api/observations'
import { Tag } from '../../../../../../api/types'
import { areQualityFeaturesEnabled } from '../../../../../../config/qualityFeatures'
import { useBuildingContext } from '../../../../../../contexts/buildingContext'
import { useProcoreAuthContext } from '../../../../../../contexts/procoreAuthContext'
import { useTagContext } from '../../../../../../contexts/tagContext'
import { useUserContext } from '../../../../../../contexts/userContext'
import { usePannellumViewer } from '../../../../../../hooks/usePannellum'
import { AnnotationManager } from '../../../../../common/Annotation/AnnotationManager'
import { ChatManager } from '../../../../../common/Chat/ChatManager'
import { checkUserPermissions } from '../../../../../common/PermissionWrapper/PermissionWrapper'
import { ProcoreManager } from '../../../../../common/Procore/ProcoreManager'
import { QualityTagManager } from '../../../../../common/QualityTag/QualityTagManager'
import { SafetyTagManager } from '../../../../../common/SafetyTag/SafetyTagManager'
import { ViewerPosition } from '../../../types'
import {
  observationIdFromTag,
  observationMatchesCurrentImage,
} from '../../../utils/safetyTagScanMatch'

const BASE_URL = process.env.REACT_APP_SERVICES_API

interface TagManagerProps {
  viewerId: string
  viewerPosition: ViewerPosition
  onTakeScreenshot(callback: (blob: Blob) => void): void
}

const isObservationBackedTag = (type: string | undefined) =>
  type === 'SAFETY' || type === 'QUALITY'

async function filterTagsForCurrentScan(
  tags: Tag[],
  projectId: string,
  currentImage: { id?: unknown; taken_on?: unknown },
): Promise<Tag[]> {
  const currentImageId = currentImage?.id
  const otherTags: Tag[] = []
  const observationTags: Tag[] = []

  for (const tag of tags) {
    if (isObservationBackedTag(tag.type)) {
      observationTags.push(tag)
    } else {
      // Point-scoped non-observation tags: keep existing image_id filter (null = show).
      if (tag.image_id == null || tag.image_id === '') {
        otherTags.push(tag)
      } else if (String(tag.image_id) === String(currentImageId)) {
        otherTags.push(tag)
      }
    }
  }

  if (!observationTags.length || !projectId) {
    return otherTags
  }

  const obsIds = Array.from(
    new Set(
      observationTags
        .map(tag => observationIdFromTag(tag))
        .filter((id): id is number => id != null),
    ),
  )

  let byId = new Map<number, Observation>()
  if (obsIds.length) {
    try {
      const rows = await fetchObservationsReportDetailsChunked(
        projectId,
        obsIds,
      )
      byId = new Map(rows.map(obs => [obs.id, obs]))
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(
        'TagManager hydrate: failed to load observations for scan filter',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (err as any)?.message || err,
      )
      // Without observation data we cannot safely show pins on every date.
      return otherTags
    }
  }

  const observationTagsForScan = observationTags.filter(tag => {
    const obsId = observationIdFromTag(tag)
    if (obsId == null) return false
    const obs = byId.get(obsId)
    if (!obs) return false
    return observationMatchesCurrentImage(obs, currentImage)
  })

  return [...otherTags, ...observationTagsForScan]
}

export const TagManager = ({
  viewerId,
  viewerPosition,
  onTakeScreenshot,
}: TagManagerProps) => {
  const { state: buildingState } = useBuildingContext()
  const { state: userState } = useUserContext()
  const {
    hydrateTags,
    findSetCurrentTag,
    toggleDisplay,
    state: tagState,
  } = useTagContext()
  const { loaded, addHotSpot, cleanTemporaryTags, clearManagedTagHotSpots } =
    usePannellumViewer(viewerId)

  const { state: procoreAuthState } = useProcoreAuthContext()
  const {
    authenticationCheckComplete,
    isAuthenticated: isProcoreAuthenticated,
  } = procoreAuthState

  const projectIsLinkedToProcore =
    buildingState &&
    buildingState.projectData &&
    buildingState.projectData.procore_company_id &&
    buildingState.projectData.procore_project_id
  const procoreTagSelected =
    tagState.tags.current?.type === 'RFI' ||
    tagState.tags.current?.type === 'PROCORE-NEW-RFI' ||
    tagState.tags.current?.type === 'PROCORE-LINK-RFI' ||
    tagState.tags.current?.type === 'PROCORE-SAVE-IMAGE'
  const showProcoreManager =
    isProcoreAuthenticated && projectIsLinkedToProcore && procoreTagSelected

  /*
  Callback for when user clicks on a hotspot.

  Don't rely on dynamic info here, only the args passed on creation essentially.
   */
  const hospotClickHandler = useCallback(
    (
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper event type
      event: any,
      args: { pitch: number; yaw: number; type: string; tagId?: number },
    ) => {
      if (args.tagId) {
        findSetCurrentTag(args.tagId)
        mixpanel.track('Click Hotspot', {
          type: args.type,
        })
      }
    },
    [findSetCurrentTag],
  )

  const hotspotContextMenuHandler = useCallback(
    (event: MouseEvent, args: { tagId: number }) => {
      if (args.tagId) findSetCurrentTag(args.tagId, true)
    },
    [findSetCurrentTag],
  )

  const hydrate = useCallback(
    (tagsToUpdate?: number[]) => {
      const currentImage = buildingState.imageData.data
      const currentImageId = currentImage?.id
      return axios
        .get(`${BASE_URL}/tags/`, {
          params: {
            isProcoreAuthenticated: isProcoreAuthenticated,
            project_id: buildingState.projectId,
            point_id: buildingState.pointId,
            image_id: currentImageId,
            safety: checkUserPermissions(
              userState.permissions,
              'safety_tables.read_observation',
              buildingState.projectId,
            ),
            quality:
              areQualityFeaturesEnabled &&
              checkUserPermissions(
                userState.permissions,
                'safety_tables.read_quality_observation',
                buildingState.projectId,
              ),
          },
        })
        .then(async res => {
          const rawTags = (res.data as Tag[]).filter(
            tag => areQualityFeaturesEnabled || tag.type !== 'QUALITY',
          )
          const tagsForImage = await filterTagsForCurrentScan(
            rawTags,
            buildingState.projectId,
            {
              id: currentImageId,
              taken_on: currentImage?.taken_on,
            },
          )
          clearManagedTagHotSpots()
          hydrateTags({ all: tagsForImage })
          tagsForImage.forEach((tag: Tag) => {
            const useDisabledStyle = tag.rfi && tag.rfi.status === 'closed'
            addHotSpot(
              [tag.pitch, tag.yaw],
              tag.type,
              hospotClickHandler,
              hotspotContextMenuHandler,
              tag.id,
              0, // Not passing angleOffset because annotations are about an image pixels, not real-world positioning
              {
                disabled: useDisabledStyle,
                tagsToUpdate: tagsToUpdate,
              },
            )
          })
          return tagsForImage
        })
        .catch(err => {
          // eslint-disable-next-line no-console
          console.warn(
            'TagManager hydrate: failed to load tags',
            err?.message || err,
          )
          return []
        })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: addHotSpot intentionally omitted to prevent infinite loops
    [
      buildingState.imageData.data?.id,
      buildingState.imageData.data?.taken_on,
      buildingState.pointId,
      buildingState.projectId,
      clearManagedTagHotSpots,
      hospotClickHandler,
      hotspotContextMenuHandler,
      hydrateTags,
      viewerId,
      isProcoreAuthenticated,
      userState.permissions,
    ],
  )

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper event type
    const onClickOutside = (event: any) => {
      if (!event.target.closest('.pnlm-ignore')) {
        toggleDisplay()
      }
      if (
        !event.target.closest('.oco-dialogue') &&
        !event.target.closest('.context-menu-li')
      ) {
        cleanTemporaryTags()
      }
    }
    document.addEventListener('click', onClickOutside)
    return () => {
      document.removeEventListener('click', onClickOutside)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: cleanTemporaryTags and toggleDisplay intentionally omitted - should only run once on mount
  }, [])

  useEffect(() => {
    if (loaded && authenticationCheckComplete) {
      hydrate().then(() => {
        const urlTag = new URLSearchParams(window.location.search).get('tag')
        if (urlTag) findSetCurrentTag(parseInt(urlTag))
      })
    }
    return
  }, [loaded, authenticationCheckComplete, findSetCurrentTag, hydrate])

  return (
    <div style={{ position: 'relative' }}>
      {tagState.tags.current?.type === 'CHAT' && (
        <ChatManager
          viewerId={viewerId}
          hydrate={hydrate}
          viewerPosition={viewerPosition}
        />
      )}
      {tagState.tags.current?.type === 'ANNOTATION' && (
        <AnnotationManager
          tag={tagState.current}
          viewerId={viewerId}
          hydrate={hydrate}
          viewerPosition={viewerPosition}
        />
      )}
      {tagState.tags.current?.type === 'SAFETY' && (
        <SafetyTagManager
          tag={tagState.current}
          viewerId={viewerId}
          hydrate={hydrate}
          viewerPosition={viewerPosition}
        />
      )}
      {areQualityFeaturesEnabled &&
        tagState.tags.current?.type === 'QUALITY' && (
          <QualityTagManager
            tag={tagState.current}
            viewerId={viewerId}
            hydrate={hydrate}
            viewerPosition={viewerPosition}
          />
        )}
      {showProcoreManager && (
        <ProcoreManager
          viewerId={viewerId}
          hydrate={hydrate}
          viewerPosition={viewerPosition}
          onTakeScreenshot={onTakeScreenshot}
        />
      )}
    </div>
  )
}
